import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Toolbar from '@material-ui/core/Toolbar';

import Typography from '@material-ui/core/Typography';
import IconButton from 'components/common/IconButton';
import Menu, { MenuItem } from 'components/common/Menu';
import IconBlurbs from 'components/icons/IconBlurbs';
import IconMoreVert from 'components/icons/IconMoreVert';
import IconPen from 'components/icons/IconPen';
import IconWatch from 'components/icons/IconWatch';
import PatientDeleteDialog from 'components/patient/PatientDeleteDialog';
import EventLogViewDialog from 'components/patient/EventLogViewDialog';
import PrintDialog from 'components/common/PrintDialog';
import DocumentsSignDialog from 'components/common/DocumentsSignDialog';
import NotificationsDialog from 'components/patient/NotificationsDialog';
import DocumentsViewDialog from 'components/common/DocumentsViewDialog';
import Watchers from 'components/patient/Watchers';

import pxToRem from 'styles/pxToRem';

const styles = theme => ({
  root: {
    maxWidth: 168,
    borderRadius: 5,
    border: `1px solid ${theme.palette.primary.A100}`,
    position: 'relative',
    '&:before': {
      content: '"."',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: '50%',
      width: 1,
      color: 'transparent',
      background: theme.palette.primary.A100,
    },
  },
  toolBar: {
    position: 'relative',
    width: '100%',
    justifyContent: 'space-around',
    '&:before': {
      content: '"."',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: '25%',
      width: 1,
      color: 'transparent',
      background: theme.palette.primary.A100,
    },
    '&:after': {
      content: '"."',
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: '25%',
      width: 1,
      color: 'transparent',
      background: theme.palette.primary.A100,
    },
  },
  iconButton: {
    margin: -4,
    color: theme.palette.grey[600],
  },
  iconButtonActive: {
    color: theme.palette.primary[500],
  },
  iconBlurbs: {
    width: 20,
    height: 20,
  },
  iconPen: {
    width: 18,
    height: 18,
  },
  iconWatch: {
    width: 24,
    height: 24,
  },
  iconMoreVert: {
    width: 24,
    height: 24,
  },
  typographyNewNotification: {
    width: 12,
    height: 12,
    position: 'absolute',
    top: -7,
    right: -4,
    borderRadius: 8,
    backgroundColor: theme.palette.notification[500],
    border: `1px solid ${theme.palette.common.white}`,
    fontSize: pxToRem(10),
    lineHeight: pxToRem(12),
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
  iconSMS: {
    position: 'relative',
    width: 20,
    height: 20,
  },
  typographySMS: {
    position: 'absolute',
    left: 1,
    top: 4,
    fontSize: pxToRem(6),
    fontWeight: 700,
    color: theme.palette.common.white,
  },
});

class PatientDetailsBarActions extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      deleteDilogOpen: false,
      eventLogViewDilogOpen: false,
      printDialogOpen: false,
      signDialogOpen: false,
      notificationsDialogOpen: false,
      printFormValues: null,
      currentViewDocument: null,
      documentViewDialogOpen: false,
      watcherMenuOpen: false,
      patientMenuOpen: false,
    };

    this.onMenuClose = this.onMenuClose.bind(this);
    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onDeleteMenuItemClick = this.onDeleteMenuItemClick.bind(this);
    this.onDeleteDialogClose = this.onDeleteDialogClose.bind(this);
    this.onEventLogViewItemClick = this.onEventLogViewItemClick.bind(this);
    this.onEventLogViewDialogClose = this.onEventLogViewDialogClose.bind(this);
    this.onPrintItemClick = this.onPrintItemClick.bind(this);
    this.onPrintDialogClose = this.onPrintDialogClose.bind(this);
    this.onPrintDialogSign = this.onPrintDialogSign.bind(this);
    this.onSignDialogClose = this.onSignDialogClose.bind(this);
    this.onNotificationsButtonClick = this.onNotificationsButtonClick.bind(this);
    this.onNotificationsDialogClose = this.onNotificationsDialogClose.bind(this);
    this.onDocumentView = this.onDocumentView.bind(this);
    this.onDocumentViewDialogClose = this.onDocumentViewDialogClose.bind(this);
    this.onWatchButtonClick = this.onWatchButtonClick.bind(this);
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onMenuButtonClick(event) {
    this.setState({
      patientMenuOpen: true,
      watcherMenuOpen: false,
      anchorEl: event.currentTarget,
    });
  }

  onDeleteMenuItemClick() {
    this.setState({
      deleteDilogOpen: true,
      anchorEl: null,
    });
  }

  onDeleteDialogClose() {
    this.setState({ deleteDilogOpen: false });
  }

  onEventLogViewItemClick() {
    this.setState({
      eventLogViewDialogOpen: true,
      anchorEl: null,
    });
  }

  onEventLogViewDialogClose() {
    this.setState({ eventLogViewDialogOpen: false });
  }

  onPrintItemClick() {
    this.setState({
      printDialogOpen: true,
      anchorEl: null,
    });
  }

  onPrintDialogClose() {
    this.setState({ printDialogOpen: false });
  }

  onPrintDialogSign(values) {
    this.setState({
      printDialogOpen: false,
      printFormValues: values,
      signDialogOpen: true,
    });
  }

  onSignDialogClose() {
    this.setState({ signDialogOpen: false });
  }

  onNotificationsButtonClick() {
    this.setState({ notificationsDialogOpen: true });
  }

  onNotificationsDialogClose() {
    this.setState({ notificationsDialogOpen: false });
  }

  onDocumentView(document) {
    this.setState({
      currentViewDocument: document,
      documentViewDialogOpen: true,
      notificationsDialogOpen: false,
    });
  }

  onDocumentViewDialogClose() {
    this.setState({
      currentViewDocument: null,
      documentViewDialogOpen: false,
      notificationsDialogOpen: true,
    });
  }

  onWatchButtonClick(event) {
    this.setState({
      patientMenuOpen: false,
      watcherMenuOpen: true,
      anchorEl: event.currentTarget,
    });
  }

  render() {
    const { account, patient, onEditPatientInformationButtonClick, business, classes } = this.props;
    const {
      anchorEl,
      deleteDilogOpen,
      eventLogViewDialogOpen,
      printDialogOpen,
      signDialogOpen,
      notificationsDialogOpen,
      printFormValues,
      currentViewDocument,
      documentViewDialogOpen,
      watcherMenuOpen,
      patientMenuOpen,
    } = this.state;
    const watchedByUser = patient.Watcher.filter(item => item.ID === account.ID).length > 0;
    return (
      <Paper className={classes.root} elevation={1}>
        <Toolbar disableGutters className={classes.toolBar}>
          <IconButton
            className={classNames(classes.iconButton, {
              [classes.iconButtonActive]: watchedByUser,
            })}
            aria-label="More"
            aria-owns={watcherMenuOpen && Boolean(anchorEl) ? `${patient['Record Number']}-watcher-menu` : null}
            aria-haspopup="true"
            onClick={this.onWatchButtonClick}
            tooltipContent="Watching"
          >
            <IconWatch className={classes.iconWatch} />
            {patient.Watcher.length > 0 &&
              <Typography variant="caption">
                {patient.Watcher.length}
              </Typography>
            }
          </IconButton>
          <Menu
            id={`${patient['Record Number']}-watcher-menu`}
            anchorEl={anchorEl}
            open={watcherMenuOpen && Boolean(anchorEl)}
            onClose={this.onMenuClose}
          >
            <Watchers
              patient={patient}
              onClose={this.onMenuClose}
            />
          </Menu>
          <IconButton
            className={classes.iconButton}
            tooltipContent="Edit Patient Information"
            onClick={onEditPatientInformationButtonClick}
          >
            <IconPen className={classes.iconPen} />
          </IconButton>
          <IconButton
            className={classes.iconButton}
            tooltipContent="Notifications"
            onClick={this.onNotificationsButtonClick}
          >
            <div className={classes.iconSMS}>
              <IconBlurbs className={classes.iconBlurbs} />
              <Typography
                className={classes.typographySMS}
              >
                SMS
              </Typography>
              {(patient['Unread SMS'] > 0 && !notificationsDialogOpen) &&
                <Typography className={classes.typographyNewNotification}>
                  {patient['Unread SMS']}
                </Typography>
              }
            </div>
          </IconButton>
          <IconButton
            className={classes.iconButton}
            aria-label="More"
            aria-owns={patientMenuOpen && Boolean(anchorEl) ? `${patient['Record Number']}-patient-menu` : null}
            aria-haspopup="true"
            onClick={this.onMenuButtonClick}
            tooltipContent="Open Menu"
          >
            <IconMoreVert className={classes.iconMoreVert} />
          </IconButton>
          <Menu
            id={`${patient['Record Number']}-patient-menu`}
            anchorEl={anchorEl}
            open={patientMenuOpen && Boolean(anchorEl)}
            onClose={this.onMenuClose}
          >
            <MenuItem
              onClick={this.onPrintItemClick}
            >
              Print Data
            </MenuItem>
            <MenuItem
              onClick={this.onEventLogViewItemClick}
            >
              View Activity
            </MenuItem>
            <MenuItem
              onClick={this.onDeleteMenuItemClick}
              color="warning"
              disabled={account.Roles.filter(item =>
                item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
            >
              Delete Patient
            </MenuItem>
          </Menu>
          <PatientDeleteDialog
            open={deleteDilogOpen}
            onClose={this.onDeleteDialogClose}
            {...patient}
          />
          <EventLogViewDialog
            open={eventLogViewDialogOpen}
            onClose={this.onEventLogViewDialogClose}
            {...patient}
            business={business}
          />
          <PrintDialog
            form={`${patient['Record Number']}-patientPrint`}
            open={printDialogOpen}
            onClose={this.onPrintDialogClose}
            onSign={this.onPrintDialogSign}
            patient={patient}
          />
          <DocumentsSignDialog
            open={signDialogOpen}
            onClose={this.onSignDialogClose}
            printFormValues={printFormValues}
            patient={patient}
            variant="patient"
          />
          <NotificationsDialog
            open={notificationsDialogOpen}
            onClose={this.onNotificationsDialogClose}
            {...patient}
            business={business}
            onDocumentView={this.onDocumentView}
          />
          {currentViewDocument && currentViewDocument.ID &&
            <DocumentsViewDialog
              open={documentViewDialogOpen}
              onClose={this.onDocumentViewDialogClose}
              Documents={[currentViewDocument]}
              initialDocumentID={currentViewDocument.ID}
              patient={patient}
            />
          }
        </Toolbar>
      </Paper>
    );
  }
}

PatientDetailsBarActions.propTypes = {
  patient: PropTypes.shape({
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
    'Middle Name': PropTypes.string,
    'Record Number': PropTypes.number.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  onEditPatientInformationButtonClick: PropTypes.func,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    toolBar: PropTypes.string.isRequired,
    iconButton: PropTypes.string.isRequired,
    iconBlurbs: PropTypes.string.isRequired,
    iconMoreVert: PropTypes.string.isRequired,
  }).isRequired,
};

PatientDetailsBarActions.defaultProps = {
  business: {},
  onEditPatientInformationButtonClick: () => {},
};

const mapStateToProps = store => ({
  account: store.account,
});

export default connect(mapStateToProps,)(
  withStyles(styles)(PatientDetailsBarActions),
);



// WEBPACK FOOTER //
// ./src/components/patient/PatientDetailsBar/PatientDetailsBarActions.js